import React, { useEffect, useRef, useState } from "react";
import { PaymentButtonSlot, PaymentFooter } from "components/PaymentButton";
import PaymentPolicyLinks, {
  PaymentPolicyHelpPanelContext,
} from "components/PaymentButton/PaymentPolicyLinks";
import Skeleton from "components/Skeleton";
import { SrOnly } from "components/SrOnly";
import { useTranslation } from "react-i18next";
import { openExternalURL } from "utils/common";
import { getPayPalPayerActionUrl, type PayPalConfig } from "utils/paypal";
import { toastResponseError } from "utils/responseMessageHelper";
import {
  PayPalButtonPlaceholder,
  PayPalButtonsContainer,
  PayPalFooterWrapper,
} from "./PayPalOrderButton.styles";

type PayPalOrderButtonProps = {
  paypalConfig?: PayPalConfig | null;
  createOrder: () => Promise<string>;
  captureOrder: (orderId: string) => Promise<void>;
  onSuccess?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  fallbackChildren: React.ReactNode;
  helpPanelContext?: PaymentPolicyHelpPanelContext;
};

type PayPalNamespace = {
  Buttons: (options: {
    createOrder: () => Promise<string>;
    onApprove: (data: { orderID: string }) => Promise<void>;
    onError: (error: unknown) => void;
    onCancel?: () => void;
    style?: Record<string, boolean | string | number>;
  }) => {
    render: (element: HTMLElement) => Promise<void>;
    close?: () => void;
  };
};

// The PayPal SDK script writes its export to `window[data-namespace]` itself
// (so multiple client-id/currency combos can coexist); this is the only spot
// that needs a dynamic, untyped window lookup, so we keep it local instead of
// widening the global Window type for the whole app.
const getNamespacedSdk = (namespace: string): PayPalNamespace | undefined =>
  (window as unknown as Record<string, PayPalNamespace | undefined>)[namespace];

const paypalNamespace = (clientId: string, currency: string) =>
  `paypal_sdk_${btoa(`${clientId}:${currency}`).replace(/[^a-zA-Z0-9]/g, "")}`;

// Thrown by a createOrder implementation to signal that the payment was
// already completed through another path (e.g. a race where the balance
// covered the total by the time the button was clicked), so the SDK's
// onError handler should stay quiet instead of surfacing a false error.
export class PayPalOrderSkippedError extends Error {}

// Cache in-flight/resolved SDK loads per namespace so concurrent mounts share
// one script tag, and drop failed attempts so a later retry (e.g. reopening
// the payment panel after a blocked request) loads a fresh script instead of
// waiting forever on load/error events that already fired on the old one.
const paypalSdkPromises = new Map<string, Promise<PayPalNamespace>>();

const loadPayPalSdk = (
  clientId: string,
  currency: string,
): Promise<PayPalNamespace> => {
  const namespace = paypalNamespace(clientId, currency);
  const existingSdk = getNamespacedSdk(namespace);
  if (existingSdk?.Buttons) return Promise.resolve(existingSdk);

  const cached = paypalSdkPromises.get(namespace);
  if (cached) return cached;

  const promise = new Promise<PayPalNamespace>((resolve, reject) => {
    document
      .querySelectorAll<HTMLScriptElement>(
        `script[data-paypal-namespace="${namespace}"]`,
      )
      .forEach((staleScript) => staleScript.remove());

    const script = document.createElement("script");
    const params = new URLSearchParams({
      "client-id": clientId,
      currency,
      intent: "capture",
      components: "buttons",
    });
    script.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
    script.async = true;
    script.dataset.namespace = namespace;
    script.dataset.paypalNamespace = namespace;
    script.addEventListener("load", () => {
      const sdk = getNamespacedSdk(namespace);
      sdk?.Buttons ? resolve(sdk) : reject(new Error("PayPal SDK unavailable"));
    });
    script.addEventListener("error", () =>
      reject(new Error("PayPal SDK failed to load")),
    );
    document.body.appendChild(script);
  }).catch((error) => {
    paypalSdkPromises.delete(namespace);
    throw error;
  });

  paypalSdkPromises.set(namespace, promise);
  return promise;
};

const PayPalOrderButton: React.FC<PayPalOrderButtonProps> = React.memo(
  ({
    paypalConfig,
    createOrder,
    captureOrder,
    onSuccess,
    disabled = false,
    isLoading = false,
    fallbackChildren,
    helpPanelContext,
  }) => {
    const { t } = useTranslation("common");
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonsRef = useRef<{ close?: () => void } | null>(null);
    // Set by our own createOrder wrapper below and read back by onError in
    // the same closure — this only ever crosses a JS closure boundary we
    // control, never the PayPal SDK, so it can't be affected by however the
    // SDK chooses to construct/forward the value it passes to onError.
    const orderSkippedRef = useRef(false);
    const handledApprovalErrorsRef = useRef<WeakSet<object>>(new WeakSet());
    const [isSdkLoading, setIsSdkLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [sdkError, setSdkError] = useState<unknown>(null);
    const clientId = paypalConfig?.client_id;
    const currency = paypalConfig?.currency;
    const canRenderButtons =
      !!clientId && !!currency && !disabled && !isLoading && !sdkError;

    useEffect(() => {
      if (
        !canRenderButtons ||
        !clientId ||
        !currency ||
        !containerRef.current
      ) {
        return undefined;
      }

      let isMounted = true;
      const container = containerRef.current;
      container.innerHTML = "";
      setIsSdkLoading(true);
      setSdkError(null);

      loadPayPalSdk(clientId, currency)
        .then(async (paypal) => {
          if (!isMounted || !containerRef.current) return;

          const buttons = paypal.Buttons({
            createOrder: async () => {
              orderSkippedRef.current = false;
              try {
                return await createOrder();
              } catch (error) {
                if (error instanceof PayPalOrderSkippedError) {
                  orderSkippedRef.current = true;
                }
                throw error;
              }
            },
            onApprove: async (data) => {
              setIsProcessing(true);
              try {
                await captureOrder(data.orderID);
                onSuccess?.();
              } catch (error) {
                const payerActionUrl = getPayPalPayerActionUrl(error);
                if (payerActionUrl) openExternalURL(payerActionUrl);
                if (error && typeof error === "object") {
                  handledApprovalErrorsRef.current.add(error);
                }
                toastResponseError(error);
                throw error;
              } finally {
                if (isMounted) setIsProcessing(false);
              }
            },
            onError: (error) => {
              if (orderSkippedRef.current) {
                orderSkippedRef.current = false;
                return;
              }
              if (
                error &&
                typeof error === "object" &&
                handledApprovalErrorsRef.current.has(error)
              ) {
                handledApprovalErrorsRef.current.delete(error);
                return;
              }
              toastResponseError(error);
            },
            style: {
              layout: "vertical",
              height: 48,
              shape: "rect",
              label: "paypal",
              disableMaxWidth: true,
            },
          });
          buttonsRef.current = buttons;
          await buttons.render(container);
        })
        .catch((error) => {
          if (!isMounted) return;

          setSdkError(error);
          toastResponseError(error);
        })
        .finally(() => {
          if (isMounted) setIsSdkLoading(false);
        });

      return () => {
        isMounted = false;
        buttonsRef.current?.close?.();
        buttonsRef.current = null;
        container.innerHTML = "";
      };
    }, [
      canRenderButtons,
      captureOrder,
      clientId,
      createOrder,
      currency,
      disabled,
      isLoading,
      onSuccess,
      sdkError,
    ]);

    if (!canRenderButtons) {
      return (
        <PaymentButtonSlot>
          <PaymentFooter
            disabled
            isLoading={isLoading}
            helpPanelContext={helpPanelContext}
          >
            {fallbackChildren}
          </PaymentFooter>
        </PaymentButtonSlot>
      );
    }

    const isPayPalControlLoading = isProcessing || isSdkLoading;
    const payPalLoadingLabel = t("common.loadingWithLabel", {
      word: "PayPal",
    });

    return (
      <PaymentButtonSlot>
        <PayPalFooterWrapper>
          {isPayPalControlLoading && (
            <PayPalButtonPlaceholder aria-live="polite" role="status">
              <Skeleton height="48px" borderRadius="4px" />
              <SrOnly>{payPalLoadingLabel}</SrOnly>
            </PayPalButtonPlaceholder>
          )}
          <PayPalButtonsContainer
            ref={containerRef}
            style={{ display: isPayPalControlLoading ? "none" : "block" }}
          />
          <PaymentPolicyLinks helpPanelContext={helpPanelContext} />
        </PayPalFooterWrapper>
      </PaymentButtonSlot>
    );
  },
);

export default PayPalOrderButton;
